import React, { useRef } from 'react';
import { Button, ButtonProps } from '../Button/Button';
import { IconButton, IconButtonProps } from '../IconButton/IconButton';
import { Box } from '../Box';
import { BpDesignSystem } from '../../types';
import { Icon } from '../Icon/Icon';
import { FormHelperText, FormHelperTextProps } from '../Form/FormHelperText';
import { useIntlBds } from '../../translations/BdsIntlProvider';

type AcceptImages = 'image/*' | '.png' | '.jpg' | '.jpeg' | '.bmp' | '.webp' | '.gif';
type AcceptText = 'text/*' | 'text/json' | '.txt' | '.json';
type AcceptDocuments = '.xlsx' | '.xls' | '.csv' | '.doc' | '.pdf' | '.docx' | '.xml';
type AcceptCompressed = '.zip';
type AcceptAll = '*/*';

export type UploadButtonAccept = AcceptImages | AcceptText | AcceptDocuments | AcceptCompressed | AcceptAll;

export type UploadButtonProps = BpDesignSystem.Props<
  {
    /**
     * @deprecated Use `accept` prop instead to specify accepted file types for upload.
     */
    whiteList?: string | undefined;
    accept?: UploadButtonAccept[];
    errorText?: string;
    type?: 'button' | 'icon';
    disabled?: boolean;
    loading?: boolean;
    slotProps?: {
      button?: Omit<ButtonProps, 'onClick' | 'disabled' | 'ref' | 'loading'>;
      icon?: Omit<IconButtonProps, 'onClick' | 'disabled' | 'ref'>;
      wrapper: Partial<BpDesignSystem.Props>;
      helperText: Partial<FormHelperTextProps>;
    };
  } & (
    | { multiple: true; onFileSelect: (files: File[]) => Promise<void> }
    | {
        multiple?: false;
        onFileSelect: (file: File) => Promise<void>;
      }
  )
>;

export const UploadButton: React.FC<UploadButtonProps> = ({
  slotProps,
  disabled,
  dataTest = 'upload-button',
  errorText,
  whiteList,
  accept = ['*/*'],
  onFileSelect,
  multiple,
  type = 'button',
  loading,
  ...rest
}) => {
  const { intl } = useIntlBds();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onUploadClick = () => fileInputRef?.current?.click();

  return (
    <Box {...slotProps?.wrapper}>
      {type === 'icon' ? (
        <IconButton
          iconName="upload"
          color="colorPrimary"
          dataTest={dataTest}
          loading={loading}
          disabled={disabled}
          {...slotProps?.icon}
          onClick={onUploadClick}
        />
      ) : (
        <Button
          variant="text"
          color="primary"
          dataTest={dataTest}
          disabled={disabled}
          startIcon={<Icon color={disabled ? 'colorSecondaryContainer' : 'colorPrimary'} name="backup" />}
          {...rest}
          loading={loading}
          onClick={onUploadClick}
        >
          {intl.DocumentField.uploadButtonLabel}
        </Button>
      )}

      <input
        style={{ display: 'none' }}
        accept={whiteList || accept.join(', ')}
        data-test="document-field-input"
        disabled={disabled}
        multiple={multiple}
        onChange={async (e) => {
          if (multiple) {
            await onFileSelect(e.currentTarget.files ? Array.from(e.currentTarget.files) : []);
          } else if (e.currentTarget.files?.[0]) {
            await onFileSelect(e.currentTarget.files?.[0]);
          }
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        ref={fileInputRef}
        type="file"
      />
      {!!errorText && (
        <FormHelperText error dataTest="upload-button-error" {...slotProps?.helperText}>
          {errorText}
        </FormHelperText>
      )}
    </Box>
  );
};
